import { Test, TestCollection, TestType } from '../../parser/models/all.js';
import { ContextLookUp } from '../../options/testParserOptions.js';

/**
 * Applies the 'only' filter expression to a range of tests within a collection.
 * Depending on the expression ('*' or a number or boolean), it marks subsequent tests in the same group as 'only'.
 * 
 * @param test - The test carrying the 'only' expression.
 * @param index - The index of the current test in the collection.
 * @param tests - The collection of tests to apply the filter to.
 */
export function applyOnlyExpression(test: Test, index: number, tests: TestCollection): void {
  let onlyCount = 0;
  const { groupName, onlyExpression } = test;

  if (onlyExpression === '*') {
    onlyCount = tests.length;
  } else if (typeof (+onlyExpression) === 'number') {
    onlyCount = index + (+onlyExpression);
  }

  onlyCount = Math.min(onlyCount, tests.length);
  for (let i = index; i < onlyCount; i++) {
    const nextTest = tests[i];

    // ensure the nextTest is in the same group
    if (nextTest.groupName != groupName) continue;

    // don't process built in functions
    if (nextTest.type.has(TestType.BUILTIN_FUNCTION)) {
      continue;
    }

    // set type if it isn't already set
    if (nextTest.isOnly === false) {
      nextTest.type.value |= TestType.HAS_ONLY
    }
  }
}

/**
 * Creates a wrapped test function that injects the appropriate context and arguments for a test case.
 * It handles varying function lengths to maintain compatibility with test runners (e.g., done callbacks).
 * 
 * @param groupName - The name of the test group.
 * @param testContextLookup - The function used to retrieve the group's context.
 * @param testFn - The original test function.
 * @param testCaseArgs - The arguments to be passed to the test function for this case.
 * @returns A new function that, when called, executes the test with the correct context and arguments.
 */
export function createTestCaseFn(groupName: string, testContextLookup: ContextLookUp, testFn: Function, testCaseArgs: any[]): Function {
  // some test runners inspect the test fn length
  // when the length is > 0 they then pass their utility args to the test fn
  // i.e. a done() callback
  if (testFn.length <= testCaseArgs.length) {
    return function(this: any) {
      const thisArg = testContextLookup(groupName);
      return testFn.call(thisArg, ...testCaseArgs);
    }
  } else if ((testFn.length - testCaseArgs.length) === 1) {
    // ensures the length of the function is at least 1
    return function(this: any, first: any, ...args: any[]) {
      const thisArg = testContextLookup(groupName);
      return testFn.call(thisArg, ...testCaseArgs, first, ...args);
    }
  } else {
    // ensures the length of the function is at least 2
    return function(this: any, first: any, second: any, ...args: any[]) {
      const thisArg = testContextLookup(groupName);
      return testFn.call(thisArg, ...testCaseArgs, first, second, ...args);
    }
  }
}
